<template>
    <slot v-if="props.isDisabled" name="trigger" v-bind="$attrs" />

    <div
        v-if="!props.isDisabled"
        v-bind="$attrs"
        ref="triggerRef"
        class="inline-flex w-max"
        @mouseover="onMouseOver"
        @mouseleave="onMouseLeave"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
        @keydown="onKeyDown"
    >
        <slot name="trigger" v-bind="triggerProps" />

        <span :id="tooltipId" class="sr-only">
            <span class="block">
                <slot name="header" />
            </span>
            <span class="block">
                <slot name="text" />
            </span>
        </span>

        <div
            v-if="isShown && !props.isTeleported"
            ref="contentRef"
            class="flex flex-col rounded-md bg-gray-50 text-center px-2 py-1 drop-shadow-[0_0_2px_rgba(0,0,0,0.25)] pointer-events-none z-60 min-w-max"
            :style="floatingStyles"
            role="tooltip"
            aria-hidden="true"
        >
            <strong
                v-if="$slots.header"
                class="block text-gray-500 uppercase text-xxs font-semibold whitespace-nowrap"
                :class="{ 'my-0.5': $slots.header }"
            >
                <slot name="header" />
            </strong>

            <div class="text-gray-900 text-sm font-light w-full min-w-max">
                <slot name="text" />
            </div>

            <svg
                ref="arrowRef"
                class="fill-gray-50 pointer-events-none"
                :style="arrowPositionStyles"
                aria-hidden="true"
                width="14"
                height="14"
                viewBox="0 0 14 14"
            >
                <path stroke="none" d="M0,0 H14 L7,7 Q7,7 7,7 Z" />
            </svg>
        </div>
    </div>

    <Teleport v-if="props.isTeleported && !props.isDisabled" v-bind="$attrs" to="body">
        <div
            v-if="isShown"
            ref="contentRef"
            class="flex flex-col rounded-md bg-gray-50 text-center px-2 py-1 drop-shadow-[0_0_2px_rgba(0,0,0,0.25)] pointer-events-none z-60 min-w-max"
            :style="floatingStyles"
            role="tooltip"
            aria-hidden="true"
        >
            <strong
                v-if="$slots.header"
                class="block text-gray-500 uppercase text-xxs font-semibold whitespace-nowrap"
                :class="{ 'my-0.5': $slots.header }"
            >
                <slot name="header" />
            </strong>

            <div class="text-gray-900 text-sm font-light w-full min-w-max">
                <slot name="text" />
            </div>

            <svg
                ref="arrowRef"
                class="fill-gray-50 pointer-events-none"
                :style="arrowPositionStyles"
                aria-hidden="true"
                width="14"
                height="14"
                viewBox="0 0 14 14"
            >
                <path stroke="none" d="M0,0 H14 L7,7 Q7,7 7,7 Z" />
            </svg>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { nanoid } from 'nanoid';
import { computed, defineComponent, inject, ref, useSlots, useTemplateRef, watch } from 'vue';
import { triggerHideTooltipKey } from './injectionKeys';

defineComponent({
    inheritAttrs: false,
});

type TooltipProps = {
    isTeleported?: boolean;
    isDisabled?: boolean;
    placement?: 'top' | 'bottom';
    viewportOverpassingReference?: boolean;
    // Shows the tooltip regardless of hover and focus, e.g. for an element highlighted via aria-activedescendant
    isOpen?: boolean;
};
const props = defineProps<TooltipProps>();

const isOpenVia = ref<'hover' | 'focus' | 'closed'>('closed');
const isShown = computed(() => props.isOpen || isOpenVia.value !== 'closed');
const tooltipId = nanoid();

const slots = useSlots();

// No `deep: true`: the watched members are slot functions, booleans and a string, none of which Vue's
// `traverse` recurses into, and the getter returns a fresh array each run so the callback fires on identity
// change either way. Deep traversal only added dep tracking, multiplied across every tooltip on the page.
watch(
    () => [slots['trigger'], slots['header'], slots['text'], props.isTeleported, props.isDisabled, props.placement],
    () => {
        isOpenVia.value = 'closed';
    },
);

const triggerProps = {
    'aria-labelledby': tooltipId,
    popovertarget: undefined, // TODO This should be set correctly once we've fixed the tooltip issue in Firefox
};

function onKeyDown(event: KeyboardEvent) {
    switch (event.key) {
        case 'Escape':
            isOpenVia.value = 'closed';
            event.preventDefault(); // Prevent bubbling which might close other things accidentally
            break;
    }
}

function onMouseOver() {
    // Mouse over shouldn't overwrite the more "explicit" focus
    if (isOpenVia.value === 'closed') {
        isOpenVia.value = 'hover';
    }
}

function onMouseLeave() {
    if (isOpenVia.value === 'hover') {
        isOpenVia.value = 'closed';
    }
}

function onFocusIn() {
    isOpenVia.value = 'focus';
}

function onFocusOut() {
    isOpenVia.value = 'closed';
}

const triggerCounter = inject(triggerHideTooltipKey, ref(-1));
watch(triggerCounter, () => {
    isOpenVia.value = 'closed';
});

const ARROW_HEIGHT = 7;
const GAP = 2;
const triggerRef = useTemplateRef('triggerRef');
const contentRef = useTemplateRef('contentRef');
const arrowRef = ref(null);

const virtualTriggerRef = computed(() => {
    if (!props.viewportOverpassingReference) {
        return triggerRef.value;
    }

    const element = triggerRef.value;

    if (!element) {
        return {
            getBoundingClientRect: () => ({
                width: 0,
                height: 0,
                x: 0,
                y: 0,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
            }),
        };
    }

    return {
        getBoundingClientRect() {
            const rect = element.getBoundingClientRect();

            // Clamp the boundaries for the tooltip to the visual part of the trigger element
            const left = Math.max(0, rect.left);
            const right = Math.min(window.innerWidth, rect.right);

            return {
                left,
                right,
                top: rect.top,
                bottom: rect.bottom,
                x: left,
                y: rect.top,
                width: Math.max(0, right - left),
                height: rect.height,
            };
        },
        contextElement: element,
    };
});

const { floatingStyles, placement, middlewareData } = useFloating(virtualTriggerRef, contentRef, {
    placement: props.placement ?? 'top',
    whileElementsMounted: autoUpdate,
    middleware: [
        offset(ARROW_HEIGHT + GAP), // number = Shorthand for main-axis
        flip({
            fallbackAxisSideDirection: 'start', // Which side to try first if neither the preferred option nor the fallback option fits
            crossAxis: false, // Don't check overflow on cross axis ==> Preserving the original axis as best as possible
        }),
        shift({ padding: 2 }), // padding = Minimal distance from tooltip to viewport edge
        arrow({
            element: arrowRef,
            padding: 8, // Prevent weird looking arrow positioning due to used border-radius
        }),
    ],
});

const arrowPositionStyles = computed(() => {
    // 14 === height of arrow
    if (placement.value === 'bottom') {
        return `
            position: absolute;
            left: ${middlewareData.value.arrow?.x}px;
            top: -14px;
            transform: rotate(180deg);
        `;
    }

    return `
        position: absolute;
        left: ${middlewareData.value.arrow?.x}px;
        bottom: -14px;
    `;
});
</script>
